import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, catchError, map, of, switchMap, tap } from 'rxjs';

import { EmptyStateComponent } from '../../../ui/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import {
  PaginationInfo,
  QuestionBankItem,
  QuestionSearchCriteria,
  QuestionSortBy,
} from '../../../../core/interfaces/question-search';
import { LearningService } from '../../../../core/services/learning-path.service';
import { QuestionSearchService } from '../../../../core/services/questions/question-search.service';
import {
  QuestionFilterLearningPath,
  QuestionFilterSubject,
  QuestionFilterTheme,
  QuestionFiltersComponent,
} from './question-filters/question-filters.component';
import { QuestionRowComponent } from './question-row/question-row.component';
import { Router } from '@angular/router';
import { QuestionService } from '../../../../core/services/question.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { toPlainText } from '../../../../shared/math/math-text';

/** Tailles de page proposées ; le back plafonne de toute façon à 200. */
const PAGE_SIZES = [25, 50, 100] as const;

/** Nombre de lignes du squelette de chargement. */
const SKELETON_ROWS = 8;

/**
 * Banque de questions : recherche à facettes paginée côté serveur.
 *
 * Le composant ne fabrique jamais de données. Une requête en échec produit un
 * état d'erreur explicite avec possibilité de réessayer ; une page légitimement
 * vide produit un état vide. Aucune liste de repli, aucun jeu de démonstration.
 */
@Component({
  selector: 'app-question-bank',
  standalone: true,
  imports: [
    TranslateModule,
    EmptyStateComponent,
    ErrorStateComponent,
    QuestionFiltersComponent,
    QuestionRowComponent,
  ],
  templateUrl: './question-bank.component.html',
  styleUrl: './question-bank.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionBankComponent implements OnInit {
  private readonly searchService = inject(QuestionSearchService);
  private readonly learningService = inject(LearningService);
  private readonly questionService = inject(QuestionService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  /** Compte Keycloak courant : sert a restreindre la taxonomie au professeur. */
  private userId: string | null = null;

  readonly pageSizes = PAGE_SIZES;
  readonly skeletonRows = Array.from({ length: SKELETON_ROWS }, (_, i) => i);
  /** Une barre grise par colonne pendant le chargement. */
  readonly skeletonCells = Array.from({ length: 10 }, (_, i) => i);

  /** Colonnes triables, dans l'ordre d'affichage de l'en-tête. */
  readonly sortableColumns: QuestionSortBy[] = [
    'title',
    'difficulty',
    'points',
    'status',
    'createdAt',
  ];

  readonly criteria = signal<QuestionSearchCriteria>({
    page: 1,
    pageSize: PAGE_SIZES[0],
    sortBy: 'createdAt',
    sortDescending: true,
  });

  readonly items = signal<QuestionBankItem[]>([]);
  readonly pagination = signal<PaginationInfo | null>(null);
  readonly loading = signal(false);
  readonly errored = signal(false);
  /** Détail technique affiché sous le message d'erreur (statut HTTP, message serveur). */
  readonly errorDetail = signal('');

  readonly learningPaths = signal<QuestionFilterLearningPath[]>([]);
  readonly subjects = signal<QuestionFilterSubject[]>([]);
  readonly themes = signal<QuestionFilterTheme[]>([]);
  readonly taxonomyErrored = signal(false);

  readonly currentPage = computed(
    () => this.pagination()?.currentPage ?? this.criteria().page ?? 1,
  );
  readonly totalPages = computed(() => this.pagination()?.totalPages ?? 0);
  readonly total = computed(() => this.pagination()?.total ?? 0);

  readonly canPrevious = computed(() => !this.loading() && this.currentPage() > 1);
  readonly canNext = computed(
    () => !this.loading() && this.currentPage() < this.totalPages(),
  );

  /** Succès mais aucun résultat : état vide, jamais de contenu de substitution. */
  readonly isEmpty = computed(
    () => !this.loading() && !this.errored() && this.items().length === 0,
  );

  private readonly searchTrigger$ = new Subject<QuestionSearchCriteria>();

  constructor() {
    // `switchMap` : une frappe rapide ou un clic répété sur « suivant » peut
    // laisser plusieurs requêtes en vol. Sans annulation, la plus lente
    // écraserait la plus récente et la table afficherait la mauvaise page.
    this.searchTrigger$
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.errored.set(false);
          this.errorDetail.set('');
        }),
        switchMap((criteria) =>
          this.searchService.search(criteria).pipe(
            map((response) => ({ ok: true as const, response })),
            catchError((error: HttpErrorResponse) =>
              of({ ok: false as const, error }),
            ),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((outcome) => {
        if (!outcome.ok) {
          this.fail(
            outcome.error.status
              ? `HTTP ${outcome.error.status}`
              : outcome.error.message,
          );
          return;
        }

        const response = outcome.response;
        if (response?.status !== 'SUCCESS') {
          // Une enveloppe en échec n'est pas une liste vide : la distinguer
          // évite d'annoncer « aucune question » alors que rien n'a été lu.
          this.fail(response?.message ?? '');
          return;
        }

        this.items.set(response.data ?? []);
        this.pagination.set(response.pagination ?? null);
        this.loading.set(false);
      });
  }

  async ngOnInit(): Promise<void> {
    this.runSearch();
    // L'identite conditionne le perimetre : on l'attend avant de charger les
    // facettes, sinon le professeur verrait fugitivement tout le referentiel.
    this.userId = await this.auth.getUserId();
    this.loadTaxonomy();
  }

  // ─── Facettes ───────────────────────────────────────────
  onCriteriaChange(next: QuestionSearchCriteria): void {
    // Changer une facette invalide la position dans la pagination : rester
    // page 7 sur un résultat qui n'en compte plus que 2 renverrait du vide.
    this.criteria.set({ ...next, page: 1 });
    this.runSearch();
  }

  // ─── Tri ────────────────────────────────────────────────
  onSort(column: QuestionSortBy): void {
    const current = this.criteria();
    const sameColumn = current.sortBy === column;
    this.criteria.set({
      ...current,
      sortBy: column,
      sortDescending: sameColumn
        ? !current.sortDescending
        : this.defaultDescending(column),
      page: 1,
    });
    this.runSearch();
  }

  isSortedBy(column: QuestionSortBy): boolean {
    return this.criteria().sortBy === column;
  }

  sortIcon(column: QuestionSortBy): string {
    if (!this.isSortedBy(column)) return 'fa-sort';
    return this.criteria().sortDescending ? 'fa-sort-down' : 'fa-sort-up';
  }

  sortAriaKey(column: QuestionSortBy): string {
    if (!this.isSortedBy(column)) return 'QUESTION_BANK.SORT.NONE';
    return this.criteria().sortDescending
      ? 'QUESTION_BANK.SORT.DESC'
      : 'QUESTION_BANK.SORT.ASC';
  }

  // ─── Pagination ─────────────────────────────────────────
  previousPage(): void {
    if (!this.canPrevious()) return;
    this.goToPage(this.currentPage() - 1);
  }

  nextPage(): void {
    if (!this.canNext()) return;
    this.goToPage(this.currentPage() + 1);
  }

  onPageSize(raw: string): void {
    const pageSize = Number(raw);
    if (!pageSize) return;
    this.criteria.update((c) => ({ ...c, pageSize, page: 1 }));
    this.runSearch();
  }

  /** Relance la dernière recherche à l'identique (bouton « Réessayer »). */
  retry(): void {
    this.runSearch();
  }

  /* ── Création d'une question ───────────────────────────── */

  /**
   * La création se fait sur une page dédiée, qui choisit elle-même sa portée
   * (niveau, matière, chapitre) et propose d'enchaîner après enregistrement.
   *
   * <p>Elle n'est plus intégrée à cet écran : la saisie de questions se fait
   * par séries, ce qu'un panneau replié au milieu d'une liste rend
   * inconfortable. Le chapitre courant est transmis en amorce, et l'URL de
   * retour ramène ici avec les mêmes filtres.</p>
   */
  createQuestion(): void {
    this.router.navigate([`${this.rolePrefix()}/questions/new`], {
      queryParams: {
        themeId: this.criteria().themeId ?? null,
        returnUrl: this.router.url,
      },
    });
  }

  /**
   * Édite une question sur la même page que la création, en mode édition.
   *
   * <p>Rappel de la règle du domaine : modifier une question déjà passée par un
   * élève ne l'écrase pas — le serveur en crée une nouvelle version. L'écran
   * d'édition n'a donc pas à le gérer, mais le prévient (la version repart en
   * brouillon).</p>
   */
  editQuestion(question: QuestionBankItem): void {
    this.router.navigate([`${this.rolePrefix()}/questions`, question.id, 'edit'], {
      queryParams: { returnUrl: this.router.url },
    });
  }

  /**
   * Supprime une question, après confirmation.
   *
   * <p>Côté serveur, une question déjà répondue n'est pas réellement effacée
   * mais archivée, pour ne pas briser les copies des élèves — l'utilisateur
   * n'a pas à le savoir, mais c'est pourquoi la ligne peut réapparaître en
   * statut « archivée » plutôt que disparaître.</p>
   */
  /* ── Sélection multiple et corbeille ──────────────────────── */

  /**
   * Ce que la liste montre : les questions en service, ou la corbeille.
   *
   * <p><b>Le défaut est « actives », et c'est une exception assumée</b> au
   * principe des filtres de cette application, où la facette « tout » ouvre
   * par défaut. L'état supprimé n'est pas une facette métier : montrer la
   * corbeille d'entrée ferait recompter des questions retirées.</p>
   */
  readonly vue = signal<'actives' | 'corbeille'>('actives');

  /** Identifiants cochés. Vidés dès que la liste change sous les pieds. */
  readonly selection = signal<ReadonlySet<number>>(new Set<number>());

  readonly enCorbeille = signal<QuestionBankItem[]>([]);
  readonly corbeilleChargement = signal(false);
  readonly operation = signal(false);

  /** La liste effectivement affichée, selon la vue. */
  readonly affichees = computed<QuestionBankItem[]>(() =>
    this.vue() === 'corbeille' ? this.enCorbeille() : this.items(),
  );

  readonly selectionCount = computed(() => this.selection().size);

  readonly toutSelectionne = computed(() => {
    const liste = this.affichees();
    return liste.length > 0 && liste.every((q) => this.selection().has(q.id));
  });

  estSelectionnee(id: number): boolean {
    return this.selection().has(id);
  }

  basculerSelection(id: number): void {
    const suivante = new Set(this.selection());
    suivante.has(id) ? suivante.delete(id) : suivante.add(id);
    this.selection.set(suivante);
  }

  /** Tout cocher, ou tout décocher si tout l'est déjà. */
  basculerTout(): void {
    this.selection.set(
      this.toutSelectionne() ? new Set<number>() : new Set(this.affichees().map((q) => q.id)),
    );
  }

  changerVue(vue: 'actives' | 'corbeille'): void {
    if (this.vue() === vue) return;

    // La sélection ne survit pas au changement de vue : les identifiants
    // cochés dans une liste n'ont rien à faire dans l'autre.
    this.selection.set(new Set<number>());
    this.vue.set(vue);

    if (vue === 'corbeille') this.chargerLaCorbeille();
  }

  chargerLaCorbeille(): void {
    this.corbeilleChargement.set(true);
    this.questionService.corbeille().subscribe({
      next: (res) => {
        this.enCorbeille.set((res?.data ?? []) as unknown as QuestionBankItem[]);
        this.corbeilleChargement.set(false);
      },
      error: () => {
        this.enCorbeille.set([]);
        this.corbeilleChargement.set(false);
      },
    });
  }

  /**
   * Met la sélection à la corbeille, et rend compte de ce qui n'a pas pu
   * partir.
   *
   * <p>Le serveur refuse les questions auxquelles un élève a répondu et
   * signale les tests qui perdent des questions. Les deux remontent à l'écran :
   * un « c'est fait » qui masquerait sept refus serait pire que le silence.</p>
   */
  mettreALaCorbeille(): void {
    const ids = [...this.selection()];
    if (ids.length === 0 || this.operation()) return;

    this.swal.confirm(
      this.translate.instant('QUESTION_BANK.TRASH_CONFIRM', { count: ids.length }),
      () => {
        this.operation.set(true);
        this.questionService.mettreALaCorbeille(ids).subscribe({
          next: (res) => {
            const bilan = res?.data;
            this.operation.set(false);
            this.selection.set(new Set<number>());
            this.runSearch();

            const partis = bilan?.deleted?.length ?? 0;
            const refuses = bilan?.refused?.length ?? 0;
            const tests = bilan?.impactedTests?.length ?? 0;

            if (refuses > 0) {
              this.swal.warning(
                this.translate.instant('QUESTION_BANK.TRASH_PARTIAL', { partis, refuses }),
              );
              return;
            }

            this.swal.success(
              tests > 0
                ? this.translate.instant('QUESTION_BANK.TRASH_DONE_TESTS', { partis, tests })
                : this.translate.instant('QUESTION_BANK.TRASH_DONE', { partis }),
            );
          },
          error: () => this.operation.set(false),
        });
      },
    );
  }

  restaurer(): void {
    const ids = [...this.selection()];
    if (ids.length === 0 || this.operation()) return;

    this.operation.set(true);
    this.questionService.restaurer(ids).subscribe({
      next: (res) => {
        this.operation.set(false);
        this.selection.set(new Set<number>());
        this.chargerLaCorbeille();
        this.swal.success(
          this.translate.instant('QUESTION_BANK.RESTORE_DONE', {
            count: res?.data?.length ?? 0,
          }),
        );
      },
      error: () => this.operation.set(false),
    });
  }

  /**
   * Vide la corbeille — définitif.
   *
   * <p>La confirmation nomme le nombre exact : « vider la corbeille » ne dit
   * pas combien on détruit, et c'est la seule action de cet écran qui ne se
   * rattrape pas.</p>
   */
  viderLaCorbeille(): void {
    if (this.operation()) return;

    const ids = [...this.selection()];
    const combien = ids.length > 0 ? ids.length : this.enCorbeille().length;
    if (combien === 0) return;

    this.swal.confirm(
      this.translate.instant('QUESTION_BANK.PURGE_CONFIRM', { count: combien }),
      () => {
        this.operation.set(true);
        this.questionService.viderLaCorbeille(ids.length > 0 ? ids : undefined).subscribe({
          next: (res) => {
            this.operation.set(false);
            this.selection.set(new Set<number>());
            this.chargerLaCorbeille();
            this.swal.success(
              this.translate.instant('QUESTION_BANK.PURGE_DONE', { count: res?.data ?? 0 }),
            );
          },
          error: () => this.operation.set(false),
        });
      },
    );
  }

  deleteQuestion(question: QuestionBankItem): void {
    this.swal.confirm(
      this.translate.instant('QUESTION_BANK.DELETE_CONFIRM', {
        // La boîte de confirmation n'affiche que du texte : formule à plat.
        title: toPlainText(question.title),
      }),
      () => {
        this.questionService.delete(question.id).subscribe({
          next: () => {
            this.swal.success(this.translate.instant('QUESTION_BANK.DELETE_SUCCESS'));
            this.runSearch();
          },
          // L'intercepteur global signale déjà l'échec réseau ou serveur.
          error: () => undefined,
        });
      },
    );
  }

  /**
   * Ouvre le compositeur de test (une seule page : portée, banque, sélection,
   * création de question). Même écran pour l'admin et l'enseignant.
   */
  composeTest(): void {
    this.router.navigate([`${this.rolePrefix()}/create-test`]);
  }

  /** Import d'un référentiel Word/PDF vers la banque. */
  importReference(): void {
    this.router.navigate([`${this.rolePrefix()}/tests/import`]);
  }

  /** Préfixe de route selon le rôle courant (admin ou enseignant). */
  private rolePrefix(): string {
    return this.router.url.startsWith('/teacher') ? '/teacher' : '/admin';
  }

  reloadTaxonomy(): void {
    this.loadTaxonomy();
  }

  // ─── Interne ────────────────────────────────────────────
  private goToPage(page: number): void {
    this.criteria.update((c) => ({ ...c, page }));
    this.runSearch();
  }

  private runSearch(): void {
    this.searchTrigger$.next(this.criteria());
  }

  private fail(detail: string): void {
    this.items.set([]);
    this.pagination.set(null);
    this.errorDetail.set(detail);
    this.errored.set(true);
    this.loading.set(false);
  }

  /** Les colonnes textuelles se lisent mieux de A à Z, les autres du plus récent / plus fort. */
  private defaultDescending(column: QuestionSortBy): boolean {
    return column !== 'title';
  }

  /**
   * Parcours, matières et thèmes viennent des parcours d'apprentissage : c'est
   * le seul endpoint qui les expose en une fois (`includeSubjects` +
   * `includeThemes`). En cas d'échec, les trois facettes sont désactivées —
   * jamais remplies avec des valeurs plausibles.
   *
   * <p>Chaque matière garde son parcours d'origine : une même matière (mêmes
   * nom et programme) existe dans plusieurs parcours sous des identifiants
   * distincts, et sans ce rattachement la liste des matières affiche des
   * doublons impossibles à départager.</p>
   */
  private loadTaxonomy(): void {
    this.taxonomyErrored.set(false);
    // Restreint au perimetre du professeur ; l'administration voit tout.
    this.learningService
      .getScopedLearningPaths(this.userId, this.auth.isAdmin(), true, true)
      .subscribe({
      next: (response) => {
        const paths = response?.data ?? [];
        const learningPaths: QuestionFilterLearningPath[] = [];
        const subjects: QuestionFilterSubject[] = [];
        const themes: QuestionFilterTheme[] = [];

        for (const path of paths) {
          learningPaths.push({ id: path.id, title: path.title });
          for (const subject of path.subjects ?? []) {
            subjects.push({
              id: subject.id,
              name: subject.name,
              learningPathId: path.id,
              learningPathTitle: path.title,
            });
            for (const theme of subject.themes ?? []) {
              themes.push({
                id: theme.id,
                title: theme.title,
                subjectId: subject.id,
              });
            }
          }
        }

        this.learningPaths.set(learningPaths);
        this.subjects.set(subjects);
        this.themes.set(themes);
      },
      error: () => {
        this.learningPaths.set([]);
        this.subjects.set([]);
        this.themes.set([]);
        this.taxonomyErrored.set(true);
      },
    });
  }
}
